import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";
import { Plug, ExternalLink, ShieldCheck } from "lucide-react";
import { PROVIDER_PRESETS } from "../lib/providerPresets";

const EMPTY = {
  id: "", name: "", base_url: "",
  auth_style: "bearer", auth_header_name: "Authorization", auth_query_param: "api_key",
  api_key: "", models: [], supports_list_models: true, notes: "",
};

export default function AddProviderDialog({ open, onOpenChange, editing = null, onSaved }) {
  const isEdit = Boolean(editing);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [presetId, setPresetId] = useState("");

  useEffect(() => {
    if (!open) return;
    if (isEdit && editing) {
      setForm({ ...EMPTY, ...editing, api_key: "" });
      setPresetId("");
    } else {
      setForm(EMPTY);
      setPresetId("");
    }
  }, [open, isEdit, editing]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function applyPreset(id) {
    setPresetId(id);
    const p = PROVIDER_PRESETS.find((x) => x.id === id);
    if (!p) return;
    setForm((f) => ({
      ...EMPTY,
      id: f.id || p.id,
      name: p.name,
      base_url: p.base_url,
      auth_style: p.auth_style,
      auth_header_name: "Authorization",
      auth_query_param: "api_key",
      models: p.default_models.slice(),
      supports_list_models: p.supports_list_models,
      notes: p.docs ? `Docs / keys: ${p.docs}` : "",
      api_key: "",
    }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.id.trim() || !form.name.trim() || !form.base_url.trim()) {
      toast.error("id, nombre y base_url son obligatorios");
      return;
    }
    if (!isEdit && !form.api_key.trim()) {
      toast.error("API key obligatoria al crear el proveedor");
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const { id, api_key, ...rest } = form;
        const patch = { ...rest };
        if (api_key.trim()) patch.api_key = api_key;
        const { data } = await api.patch(`/providers/${encodeURIComponent(editing.id)}`, patch);
        saved = data;
      } else {
        const { data } = await api.post("/providers", form);
        saved = data;
      }
      toast.success(isEdit ? "Proveedor actualizado" : "Proveedor creado");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const currentPreset = PROVIDER_PRESETS.find((p) => p.id === presetId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.providers.dialog}
        className="bg-zinc-950 border border-zinc-800 text-zinc-100 sm:max-w-[640px] max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Plug className="h-4 w-4" />
            {isEdit ? `Editar proveedor — ${editing.id}` : "Nuevo proveedor IA"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Tus apps llamarán a{" "}
            <code className="font-mono text-zinc-300">POST /api/providers/{"{id}"}/chat</code>{" "}
            y KRAI API traducirá al agregador (OpenAI-compatible).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {!isEdit && (
            <div className="rounded-md border border-zinc-800 bg-black/40 p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">
                Presets
              </div>
              <div className="flex flex-wrap gap-2">
                {PROVIDER_PRESETS.map((p) => (
                  <button
                    key={p.id} type="button"
                    onClick={() => applyPreset(p.id)}
                    className={
                      "text-xs rounded-full px-3 py-1 border transition-colors " +
                      (presetId === p.id
                        ? "border-white bg-white text-black"
                        : "border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-300")
                    }
                  >
                    {p.name}
                  </button>
                ))}
              </div>
              {currentPreset?.docs && (
                <a href={currentPreset.docs} target="_blank" rel="noreferrer"
                   className="mt-3 inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-white">
                  <ExternalLink className="h-3 w-3" /> Obtener API key en {currentPreset.name}
                </a>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="ID (único)">
              <Input
                data-testid={TID.providers.fId} disabled={isEdit}
                value={form.id} onChange={(e) => set("id", e.target.value)}
                placeholder="openrouter" pattern="[a-zA-Z0-9_\-]+" maxLength={64}
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </Field>
            <Field label="Nombre visible">
              <Input
                data-testid={TID.providers.fName}
                value={form.name} onChange={(e) => set("name", e.target.value)}
                placeholder="OpenRouter"
                className="bg-zinc-900 border-zinc-800 text-sm"
              />
            </Field>
          </div>

          <Field label="Base URL (OpenAI-compatible)">
            <Input
              data-testid={TID.providers.fBase}
              value={form.base_url} onChange={(e) => set("base_url", e.target.value)}
              placeholder="https://openrouter.ai/api/v1"
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Método de auth">
              <Select value={form.auth_style} onValueChange={(v) => set("auth_style", v)}>
                <SelectTrigger data-testid={TID.providers.fAuth}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="bearer">Bearer</SelectItem>
                  <SelectItem value="header">Header custom</SelectItem>
                  <SelectItem value="query">Query param</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.auth_style === "header" && (
              <Field label="Header">
                <Input
                  data-testid={TID.providers.fHeader}
                  value={form.auth_header_name}
                  onChange={(e) => set("auth_header_name", e.target.value)}
                  placeholder="X-Api-Key"
                  className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                />
              </Field>
            )}
            {form.auth_style === "query" && (
              <Field label="Query param">
                <Input
                  data-testid={TID.providers.fQuery}
                  value={form.auth_query_param}
                  onChange={(e) => set("auth_query_param", e.target.value)}
                  placeholder="api_key"
                  className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                />
              </Field>
            )}
          </div>

          <div className="rounded-md border border-emerald-900/40 bg-emerald-950/20 p-4 space-y-2">
            <div className="text-[11px] text-emerald-300 flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" />
              API key cifrada (AES-256-GCM) en KRAI API — nunca vuelve al navegador
            </div>
            <Field
              label={isEdit ? "API key (dejar vacío para conservar)" : "API key"}
              hint={isEdit && editing?.has_api_key ? `guardada: ${editing.api_key_masked}` : undefined}
            >
              <Input
                data-testid={TID.providers.fKey} type="password"
                value={form.api_key} onChange={(e) => set("api_key", e.target.value)}
                placeholder={currentPreset?.key_hint || (isEdit ? "sin cambios" : "sk-...")}
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                autoComplete="new-password"
              />
            </Field>
          </div>

          <Field label="Notas">
            <Textarea
              data-testid={TID.providers.fNotes}
              value={form.notes} onChange={(e) => set("notes", e.target.value)}
              rows={2}
              className="bg-zinc-900 border-zinc-800 text-sm"
            />
          </Field>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}
              className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800">
              Cancelar
            </Button>
            <Button type="submit" data-testid={TID.providers.submit} disabled={saving}
              className="bg-white text-black hover:bg-zinc-200">
              {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear proveedor"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-zinc-300 flex items-center gap-2">
        {label}
        {hint && <span className="text-[11px] font-mono text-emerald-400">{hint}</span>}
      </Label>
      {children}
    </div>
  );
}
